import { refreshAccessToken } from '../services/authService';
import { isTokenExpired } from './jwtDecoder';
import { getItem } from './authStorage';

function parseUtcDate(value) {
  if (!value) return null;
  if (value instanceof Date) return value;
  let str = String(value).trim();
  if (str.includes('T') && !str.endsWith('Z') && !str.endsWith('z') && !str.includes('+') && !str.slice(10).includes('-')) {
    str += 'Z';
  } else if (!str.includes('T') && !str.includes('Z') && str.includes(' ')) {
    str = str.replace(' ', 'T') + 'Z';
  }
  const d = new Date(str);
  return isNaN(d.getTime()) ? null : d;
}

export function formatFriendlyTimestamp(isoString) {
  const alertDate = parseUtcDate(isoString);
  if (!alertDate) return '—';
  const now = new Date();

  const alertDay = alertDate.getDate();
  const alertMonth = alertDate.getMonth();
  const alertYear = alertDate.getFullYear();

  const nowDay = now.getDate();
  const nowMonth = now.getMonth();
  const nowYear = now.getFullYear();

  const timeString = alertDate.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });

  if (alertDay === nowDay && alertMonth === nowMonth && alertYear === nowYear) {
    return `Today ${timeString}`;
  }

  const yesterday = new Date(now);
  yesterday.setDate(nowDay - 1);

  if (
    alertDay === yesterday.getDate() &&
    alertMonth === yesterday.getMonth() &&
    alertYear === yesterday.getFullYear()
  ) {
    return `Yesterday ${timeString}`;
  }

  const dateString = alertDate.toLocaleDateString([], {
    month: 'short',
    day: 'numeric'
  });

  return `${dateString} ${timeString}`;
}

export function relativeTimeFrom(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "N/A";

  const diffMs = Date.now() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  if (diffMins < 1) return "Just now";
  if (diffMins === 1) return "1 min ago";
  if (diffMins < 60) return `${diffMins} mins ago`;

  const diffHours = Math.floor(diffMins / 60);
  if (diffHours === 1) return "1 hour ago";
  if (diffHours < 24) return `${diffHours} hours ago`;

  const diffDays = Math.floor(diffHours / 24);
  if (diffDays === 1) return "1 day ago";
  if (diffDays <= 30) return `${diffDays} days ago`;

  if (diffDays <= 60) return "About a month ago";
  return "More than a month ago";
}

export function getAuthHeaders() {
  const token = getItem('accessToken');
  return {
    ...(token && token !== 'null' && token !== 'undefined' ? { Authorization: `Bearer ${token}` } : {}),
    'Content-Type': 'application/json'
  };
}

export async function ensureValidToken() {
  const token = getItem('accessToken');
  if (token && isTokenExpired(token)) {
    await refreshAccessToken();
  }
}

export async function fetchWithAuthRetry(url, options = {}) {
  let response = await fetch(url, options);
  if (response.status === 403 || response.status === 401) {
    try {
      const newAccessToken = await refreshAccessToken();
      const updatedOptions = {
        ...options,
        headers: {
          ...options.headers,
          Authorization: `Bearer ${newAccessToken}`,
        },
      };
      response = await fetch(url, updatedOptions);
    } catch (refreshError) {
      console.error('Token refresh failed during retry:', refreshError);
      return new Response(
        JSON.stringify({ error: 'Session expired or unauthorized. Please log in again.' }),
        {
          status: 401,
          statusText: 'Unauthorized',
          headers: { 'Content-Type': 'application/json' },
        }
      );
    }
  }
  return response;
}

export const ROLES = {
  ALL: ['root', 'admin', 'operator', 'viewer'],
  ADMIN_ONLY: ['root', 'admin'],
};

export function getUserRole() {
  return (getItem('role') || '').toLowerCase();
}

export function hasRole(allowedRoles = []) {
  const role = getUserRole();
  return allowedRoles.includes(role);
}

export function isDeviceOnline(device) {
  if (!device) return false;
  const status = device?.status?.toLowerCase() || "";
  return status === "active" || status === "streaming";
}

export function getDroneStatus(drone) {
  if (!drone) return "N/A";

  if (drone.is_active && drone.is_available && drone.is_in_flight && drone.is_hovering) {
    return "Hovering";
  }
  if (drone.is_active && drone.is_available && drone.is_in_flight && !drone.is_hovering) {
    return "Flight";
  }
  if (drone.is_in_flight) {
    return "Flight";
  }
  if (drone.is_active && drone.is_available) {
    return "Available";
  }
  if (drone.is_active) {
    return "Active";
  }
  return "Inactive";
}

export function formatTimestamp(value) {
  const d = new Date(value);
  if (isNaN(d.getTime())) return "\u2014";
  return d.toISOString().replace("T", " ").slice(0, 19);
}

export function formatLocalTime(dateString) {
  const d = parseUtcDate(dateString);
  if (!d) return '\u2014';
  return d.toLocaleTimeString([], {
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  });
}

export function formatChatTimestamp(dateString) {
  const d = parseUtcDate(dateString);
  if (!d) return '\u2014';
  const month = d.getMonth() + 1;
  const day = d.getDate();
  const time = formatLocalTime(dateString);
  return `${month}/${day} - ${time}`;
}

export function formatLocalDateTime(dateString) {
  try {
    const date = parseUtcDate(dateString);
    if (!date) return '\u2014';
    const dd = String(date.getDate()).padStart(2, "0");
    const mm = String(date.getMonth() + 1).padStart(2, "0");
    const yyyy = date.getFullYear();
    let hh = date.getHours();
    const min = String(date.getMinutes()).padStart(2, "0");
    const ampm = hh >= 12 ? "PM" : "AM";
    hh = hh % 12 || 12;
    return `${dd}-${mm}-${yyyy} ${String(hh).padStart(2, "0")}:${min} ${ampm}`;
  } catch {
    return '\u2014';
  }
}
